import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Checkbox, CountBadge } from '@/components/ui';
import { Tooltip } from '@/components/ui/Tooltip';
import { InboxPanel } from '@/components/notifications/inbox/InboxPanel';
import { cx } from '@/lib/cx';
import { useAuth } from '@/lib/use-auth';
import { useNotificationInbox } from '@/lib/use-notification-inbox';
import { useTaskReminders } from '@/lib/use-task-reminders';
import type {
  ReminderAudioState,
  ReminderPermission,
  ReminderToast,
  ReminderToastVariant,
} from '@/lib/use-task-reminders';

/**
 * The reminder surface: a bell in the shell, its settings panel, and the toast
 * a task falling due raises.
 *
 * Mounted ONCE, app-wide, inside the authenticated shell — the hook behind it
 * owns a poll loop and a timer per task, and a second copy would double both
 * against a rate limit the whole office shares.
 *
 * Two things it deliberately refuses to do:
 *  - **Ask for notification permission on mount.** The prompt is behind a
 *    button in the panel — a permission dialog on page load is denied
 *    reflexively and a denial cannot be re-asked.
 *  - **Imply reminders survive the tab.** There is no push and no service
 *    worker here, so the caveat sits permanently beside the toggle rather
 *    than in a tooltip.
 *
 * The toast stack sits top centre, below the app bar, and is deliberately NOT
 * a modal: no backdrop, no focus trap, `pointer-events` only on the cards
 * themselves, so the page underneath stays usable however many reminders are
 * up. Dismissible with Escape as well as the button.
 *
 * The panel also carries a **test reminder** and a plain-words readout of
 * every link in the chain — permission, and whether the browser has let this
 * page make a sound yet — because otherwise "no reminder arrived" and "no
 * task was due" look identical from the outside.
 *
 * ── THE INBOX (2026-10-16) ──────────────────────────────────────────────
 *
 * The bell is ONE panel with two tabs: "Notifications" (the in-app inbox,
 * `use-notification-inbox.ts`, rendered by `notifications/inbox/InboxPanel`)
 * and "Tasks" (everything below, behaviour unchanged). The badge is unread
 * notifications plus overdue tasks; each tab shows its own count.
 *
 * When the inbox is unavailable — an older server answering 404, or a support
 * session — the tab row disappears and the panel is exactly the old Tasks
 * panel.
 */

type BellTab = 'notifications' | 'tasks';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled])';

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function BellIcon() {
  return (
    <svg {...iconProps} className="h-5 w-5">
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

function BellOffIcon() {
  return (
    <svg {...iconProps} className="h-5 w-5">
      <path d="M8.7 3.7A6 6 0 0 1 18 8c0 2.2.3 3.9.7 5.2" />
      <path d="M17 17H3s3-2 3-9a6 6 0 0 1 .3-1.9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg {...iconProps} className="h-4 w-4 shrink-0">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** Sized for the toast, which is this icon's only caller. */
function ClockIcon() {
  return (
    <svg {...iconProps} className="h-6 w-6 shrink-0">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** Shared shape for the two link-shaped controls, since `Button` is a `<button>`. */
const LINK_BUTTON =
  'inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors duration-150 ease-out motion-reduce:transition-none max-sm:h-11';

/* ------------------------------------------------------------------ *
 * Permission — all four states, each said plainly.
 * ------------------------------------------------------------------ */

function PermissionRow({
  permission,
  onRequest,
}: {
  permission: ReminderPermission;
  onRequest: () => void;
}) {
  if (permission === 'granted') {
    return (
      <Badge tone="success" dot>
        Desktop notifications on
      </Badge>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="space-y-1">
        <Badge tone="warning" dot>
          Desktop notifications blocked
        </Badge>
        {/*
         * No retry button, on purpose. Once a site is denied, the browser gives
         * it no way to ask again — a "Try again" here would do nothing and read
         * as a bug in this app rather than a rule of the browser.
         */}
        <p className="text-xs text-fg-muted">
          Turn them on in site settings, beside the address bar. Reminders still show in the app.
        </p>
      </div>
    );
  }

  if (permission === 'unsupported') {
    return (
      <Badge tone="neutral" dot>
        Desktop notifications unavailable
      </Badge>
    );
  }

  return (
    <Button variant="secondary" size="sm" onClick={onRequest} className="w-full justify-center">
      Allow desktop notifications
    </Button>
  );
}

/* ------------------------------------------------------------------ *
 * Sound — the other link in the chain, and the one nobody expects.
 * ------------------------------------------------------------------ */

/*
 * Badges only. Every explanatory audio sentence was removed in the 2026-09-05
 * sweep, the "click anywhere to unlock the chime" recovery included — the
 * ruling kept a recovery sentence only on a browser PERMISSION state. The
 * badge wording is what is left to carry each state.
 */
function AudioRow({ audio, chime }: { audio: ReminderAudioState; chime: boolean }) {
  if (audio === 'unsupported') {
    return (
      <Badge tone="neutral" dot>
        Chime unavailable
      </Badge>
    );
  }

  if (audio === 'locked') {
    return (
      <Badge tone="warning" dot>
        Sound not unlocked yet
      </Badge>
    );
  }

  return (
    <Badge tone={chime ? 'success' : 'neutral'} dot>
      {chime ? 'Chime ready' : 'Chime ready, but muted'}
    </Badge>
  );
}

/* ------------------------------------------------------------------ *
 * Toasts.
 * ------------------------------------------------------------------ */

/**
 * The word above the title. It carries the meaning on its own — a toast that
 * says "Task due now" for something that is only a test is worse than no test
 * button at all.
 */
const TOAST_EYEBROW: Record<ReminderToastVariant, string> = {
  due: 'Task due now',
  'catch-up': 'Came due while you were away',
  test: 'Test reminder — nothing is due',
};

/**
 * One reminder card.
 *
 * Sized to be seen from across a desk rather than leaned into. Escape
 * dismisses it whenever focus is inside, and `preventDefault` marks the key
 * as spent so the stack-wide handler does not then close a second card.
 */
function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ReminderToast;
  onDismiss: (id: string) => void;
}) {
  const isTest = toast.variant === 'test';

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    onDismiss(toast.id);
  }

  return (
    <div
      role="group"
      aria-label={`${TOAST_EYEBROW[toast.variant]}: ${toast.title}`}
      onKeyDown={onKeyDown}
      className="pointer-events-auto overflow-hidden rounded-lg border border-border bg-surface-raised shadow-lg"
    >
      <div className="flex items-start gap-3 px-4 pt-4 sm:gap-4 sm:px-5 sm:pt-5">
        <span className={cx('mt-0.5', isTest ? 'text-fg-muted' : 'text-warning')}>
          <ClockIcon />
        </span>
        <div className="min-w-0 flex-1">
          {/* The word carries the meaning; the tone only reinforces it. */}
          <p className="text-xs font-medium text-fg-muted">
            {TOAST_EYEBROW[toast.variant]}
          </p>
          <p className="mt-1 text-lg leading-snug font-semibold text-fg [overflow-wrap:anywhere] sm:text-xl">
            {toast.title}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-fg-secondary [overflow-wrap:anywhere]">
            {toast.detail}
          </p>
        </div>
        <Button
          variant="ghost"
          iconOnly
          aria-label={`Dismiss ${toast.title}`}
          onClick={() => onDismiss(toast.id)}
          leadingIcon={<CloseIcon />}
        />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 px-4 pt-3 pb-4 sm:px-5 sm:pb-5">
        {isTest ? (
          // Nothing to open: the task does not exist. Dismissing IS the action.
          <Button variant="secondary" onClick={() => onDismiss(toast.id)}>
            Dismiss
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onDismiss(toast.id)}>
              Dismiss
            </Button>
            <Link
              to="/tasks"
              onClick={() => onDismiss(toast.id)}
              className={cx(
                LINK_BUTTON,
                'border-transparent bg-accent-brand text-fg-on-accent hover:bg-accent-brand-hover active:bg-accent-brand-strong',
              )}
            >
              Open
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The component.
 * ------------------------------------------------------------------ */

export function TaskReminders(): ReactElement | null {
  const { user } = useAuth();
  const navigate = useNavigate();

  // A clicked desktop notification lands here: focus the tab, then show the
  // queue it was talking about.
  const onActivate = useCallback(() => {
    navigate('/tasks');
  }, [navigate]);

  const {
    overdueCount,
    todayCount,
    error,
    enabled,
    setEnabled,
    chime,
    setChime,
    permission,
    requestPermission,
    audio,
    sendTestReminder,
    toasts,
    dismissToast,
  } = useTaskReminders({ onActivate });

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<BellTab>('notifications');
  const inbox = useNotificationInbox({ listActive: open && tab === 'notifications' });
  const showInbox = inbox.available !== false;
  const activeTab: BellTab = showInbox ? tab : 'tasks';
  const panelId = useId();
  const tabsId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  /*
   * Escape dismisses the newest reminder from anywhere on the page.
   *
   * The stack is not a focus trap by design, so without this the only way to
   * clear a card from the keyboard is to Tab all the way to the end of the
   * document and back. Two guards keep it from stealing the key from something
   * that owns it: `defaultPrevented` covers the settings panel and a focused
   * card (both call `preventDefault`), and an open `role="dialog"` — every
   * `Modal` and `ConfirmDialog` in this app — keeps Escape for itself.
   */
  useEffect(() => {
    const newestId = toasts[toasts.length - 1]?.id;
    if (newestId === undefined) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[role="dialog"]')) return;
      dismissToast(newestId);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toasts, dismissToast]);

  // Tabbing out closes the panel without swallowing the keypress, so focus
  // carries on to whatever follows the trigger.
  function onRootBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }

  function onPanelKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    setOpen(false);
    triggerRef.current?.focus();
  }

  // Signed out: no bell, no poll, nothing on screen. Hooks above run either
  // way, and the hook itself fetches nothing without a user.
  if (!user) return null;

  const latest = toasts[toasts.length - 1] ?? null;
  const unread = showInbox ? (inbox.unreadCount ?? 0) : 0;
  const overdueShown = enabled ? overdueCount : 0;
  const badgeCount = unread + overdueShown;
  const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);
  const taskLabel = enabled
    ? overdueCount > 0
      ? `Task reminders — ${overdueCount} overdue`
      : 'Task reminders'
    : 'Task reminders (off)';
  const triggerLabel = showInbox
    ? [`Notifications${unread > 0 ? ` — ${unread} unread` : ''}`, taskLabel].join(', ')
    : taskLabel;

  const bellTabs: { key: BellTab; label: string; count: number }[] = [
    { key: 'notifications', label: 'Notifications', count: unread },
    { key: 'tasks', label: 'Tasks', count: overdueShown },
  ];

  return (
    <div ref={rootRef} className="relative" onBlur={onRootBlur}>
      <Tooltip label={showInbox ? 'Notifications' : 'Task reminders'}>
        <Button
          ref={triggerRef}
          variant="ghost"
          onClick={() => setOpen((prev) => !prev)}
          aria-label={triggerLabel}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          leadingIcon={enabled || showInbox ? <BellIcon /> : <BellOffIcon />}
          className="gap-1.5 px-1.5 sm:px-2"
        >
          {badgeCount > 0 && (
            <span
              aria-hidden="true"
              className="tabular inline-flex min-w-5 items-center justify-center rounded-full bg-danger-soft px-1 text-2xs leading-4 font-semibold text-danger"
            >
              {badgeLabel}
            </span>
          )}
        </Button>
      </Tooltip>

      {/*
       * A live region that is always mounted — inserting one at the same moment
       * as its first message is the classic way to have nothing announced.
       */}
      <p role="status" aria-live="polite" className="sr-only">
        {latest ? `${TOAST_EYEBROW[latest.variant]}: ${latest.title}` : ''}
      </p>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          onKeyDown={onPanelKeyDown}
          className={cx(
            'absolute top-full right-0 z-30 mt-1 overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md',
            showInbox
              ? // Phones: pinned under the app bar with a 12px gutter, since the
                // bell is not at the bar's right edge and a right-anchored
                // 24rem panel would run off the left of a 320px screen.
                'w-[min(24rem,calc(100vw-1.5rem))] max-sm:fixed max-sm:inset-x-3 max-sm:top-14 max-sm:w-auto'
              : 'w-[min(20rem,calc(100vw-1.5rem))]',
          )}
        >
          {showInbox && (
            <div
              role="tablist"
              aria-label="Bell"
              className="flex gap-0.5 border-b border-border bg-surface-sunken p-1"
            >
              {bellTabs.map((item) => {
                const selected = activeTab === item.key;
                return (
                  <Button
                    key={item.key}
                    type="button"
                    role="tab"
                    id={`${tabsId}-${item.key}`}
                    aria-selected={selected}
                    aria-controls={`${tabsId}-${item.key}-panel`}
                    size="sm"
                    variant={selected ? 'secondary' : 'ghost'}
                    className="flex-1 justify-center gap-1.5"
                    onClick={() => setTab(item.key)}
                  >
                    <span className="whitespace-nowrap">{item.label}</span>
                    <CountBadge count={item.count} />
                  </Button>
                );
              })}
            </div>
          )}

          {activeTab === 'notifications' ? (
            <div
              role="tabpanel"
              id={`${tabsId}-notifications-panel`}
              aria-labelledby={`${tabsId}-notifications`}
            >
              <InboxPanel inbox={inbox} onNavigate={() => setOpen(false)} />
            </div>
          ) : (
          <div
            {...(showInbox
              ? {
                  role: 'tabpanel',
                  id: `${tabsId}-tasks-panel`,
                  'aria-labelledby': `${tabsId}-tasks`,
                }
              : {})}
          >
          <div className="border-b border-border px-3 py-2.5">
            <p className="text-sm font-medium text-fg">Task reminders</p>
            <Link
              to="/tasks"
              onClick={() => setOpen(false)}
              className="mt-1.5 flex flex-wrap items-center gap-1.5 rounded-sm text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline"
            >
              <Badge tone={overdueCount > 0 ? 'danger' : 'neutral'} dot={overdueCount > 0}>
                {overdueCount} overdue
              </Badge>
              <Badge tone="neutral">{todayCount} due today</Badge>
              <span className="ml-auto">My tasks &rarr;</span>
            </Link>
          </div>

          <div className="space-y-3 px-3 py-3">
            <Checkbox
              label="Remind me when a task falls due"
              checked={enabled}
              onChange={(event) => setEnabled(event.currentTarget.checked)}
            />

            {/*
             * No description: it described the chime's own mechanics, which is
             * helper text. Nothing is lost — the nesting already says a chime
             * is the SOUND and the checkbox above it is the reminder, so
             * muting cannot read as switching reminders off.
             */}
            <Checkbox
              label="Play a chime"
              checked={chime}
              disabled={!enabled}
              onChange={(event) => setChime(event.currentTarget.checked)}
            />

            {/*
             * THE DIAGNOSTIC. One button that walks the whole chain, and beside
             * it the actual state of each link — because "I set a task and
             * nothing happened" is indistinguishable from "nothing was due"
             * until someone can prove which one it was.
             */}
            <div className="space-y-2.5 border-t border-border pt-3">
              <p className="text-xs font-medium text-fg-muted">
                Check it works
              </p>

              {/*
               * Primary, and not by accident: it has to be visibly distinct
               * from the secondary "Allow desktop notifications" button that can
               * sit right beneath it, or the two read as one repeated control.
               */}
              <Button
                variant="primary"
                size="sm"
                onClick={sendTestReminder}
                className="w-full justify-center"
              >
                Send a test reminder
              </Button>

              <PermissionRow permission={permission} onRequest={requestPermission} />
              <AudioRow audio={audio} chime={chime} />
            </div>

            {/*
              ⚠️ THE TAB-OPEN CAVEAT IS GONE FROM THIS PANEL — removed in the
              2026-09-05 sweep, which keeps only errors, refusals, ConfirmDialog
              and EmptyState copy. Nothing now tells a reader that reminders are
              a timer in this tab and stop when it is closed. `TAB_OPEN_CAVEAT`
              still exists in `src/lib/use-task-reminders.ts` (outside this
              sweep's scope) and has no remaining render site.
            */}
            {error && (
              <p role="alert" className="text-xs text-danger [overflow-wrap:anywhere]">
                Couldn&rsquo;t refresh your tasks: {error}
              </p>
            )}
          </div>
          </div>
          )}
        </div>
      )}

      {/*
       * Portalled to the body: the shell's header is a stacking context, and a
       * toast rendered inside it would sit under the mobile nav drawer.
       *
       * TOP CENTRE, and clear of the app bar. `top-16` is 64px against the
       * shell header's `h-14` (56px), so the bar's own controls — the menu
       * button, search, this very bell — stay clickable with reminders up.
       * The width is `min(34rem, 100vw - 1.5rem)`, so it is a wide card on a
       * desktop and full width minus a 12px gutter at 320px, never a fixed
       * pixel width that overflows.
       *
       * `pointer-events-none` on the stack with `pointer-events-auto` on each
       * card is what keeps this from behaving like a modal: the gaps between
       * cards, and everything either side of them, click straight through to
       * the page.
       */}
      {toasts.length > 0 &&
        createPortal(
          <div
            role="region"
            aria-label="Task reminders"
            className="pointer-events-none fixed top-16 left-1/2 z-50 flex w-[min(34rem,calc(100vw-1.5rem))] -translate-x-1/2 flex-col gap-2"
          >
            {/*
             * Newest first. The hook appends and keeps the last `MAX_TOASTS`,
             * so the tail is the newest; at the top of the screen the newest
             * belongs at the top of the stack, nearest the eye.
             */}
            {[...toasts].reverse().map((toast) => (
              <ToastCard key={toast.id} toast={toast} onDismiss={dismissToast} />
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
